import { ReactElement, useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table, TableBody, TableCell,
  TableHead, TableRow, TableSortLabel
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useLazyFetch } from "../../system/useFetch";
import { IColumn, ISessionStatistics, Order } from "../../types";
import { Alert } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import {
  formatDateTimeWithSecondsX,
  getComparator,
  stableSort
} from "../../system/utils";
import StyledTableRow from "../../components/styledTableRow";
import TableContainer  from "../../components/tableContainer";

interface IProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const columns: IColumn[] = [
  {
    id: "fullName",
    label: "Full Name",
    minWidth: 170
  },
  {
    id: "email",
    label: "EMail",
    minWidth: 200
  },
  {
    id: "totalSessions",
    label: "Total Sessions",
    minWidth: 100,
    align: "right"
  },
  {
    id: "totalQueries",
    label: "Total Queries",
    minWidth: 100,
    align: "right",
  },
  {
    id: "idle",
    label: "Idle [min]",
    minWidth: 100,
    align: "right",
    format: value => value.toFixed(1)
  },
  {
    id: "lastSessionBeginAt",
    label: "Last Session",
    minWidth: 200
  },
  {
    id: "lastSessionQueryAt",
    label: "Last Query ",
    minWidth: 200,
  }
];

const SessionStatistics = ({ open, setOpen }: IProps ): ReactElement => {
  const [order, setOrder] = useState<Order>("desc");
  const [orderBy, setOrderBy] = useState<keyof ISessionStatistics>("lastSessionBeginAt");
  const [getData, { loading, error, data }] = useLazyFetch<ISessionStatistics[]>(true);

  useEffect(() => {
    if (open) {
      getData({ module: "security", query: "getSessionStatistics" });
    }
  }, [open]);


  const onClosed = () => {
    setOpen(false);
  };

  const createSortHandler = (property: keyof ISessionStatistics) => () => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const showData = !loading && !error;

  return (
    <Dialog onClose={onClosed} aria-labelledby="customized-dialog-title" open={open} fullWidth={true} maxWidth={"lg"}>
      <DialogTitle id="customized-dialog-title">
        Sessions
      </DialogTitle>
      <DialogContent dividers={true} >
        {loading && <div className="text-center"><CircularProgress /></div> }
        {error && <Alert severity="warning">{error.message}</Alert>}
        {showData && data && data.length === 0 &&  <Alert severity="warning">No Data to Display.</Alert>}

        {showData && data && data.length > 0 &&
          <>
            <TableContainer style={{ maxHeight: 582 }}>
              <Table stickyHeader={true}  size={"small"}>
                <TableHead >
                  <TableRow >
                    {columns.map((column) =>
                      <TableCell
                          style={{ backgroundColor: "#b0bec5",  minWidth: column.minWidth  }}
                        key={column.id}
                        align={column.align}
                        sortDirection={orderBy === column.id ? order : false}>
                        <TableSortLabel
                          active={orderBy === column.id}
                          direction={orderBy === column.id ? order : "asc"}
                          onClick={createSortHandler(column.id as keyof ISessionStatistics)}
                        >
                          {column.label}
                          {orderBy === column.id
                            ? <span className="cell-visuallyHidden">
                              {order === "desc" ? "sorted descending" : "sorted ascending"}
                            </span>
                            : null}
                        </TableSortLabel>
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stableSort<ISessionStatistics>(data, getComparator(order, orderBy)).map((row: any, index: number) => {
                    return (
                      <StyledTableRow hover={true} role="checkbox" tabIndex={-1} key={`alarm-row-${index}`}>
                        {columns.map((column: IColumn) => {
                          if (column.id === "status") {
                            return (

                              <TableCell key={column.id} align={column.align}/>
                            );
                          }

                          const value = row[column.id];

                          if (column.id ===  "lastSessionBeginAt" || column.id ===  "lastSessionQueryAt") {
                            return (
                              <TableCell key={column.id} align={column.align}>
                                {formatDateTimeWithSecondsX(value)}
                              </TableCell>
                            );
                          }

                          return (
                            <TableCell key={column.id} align={column.align}>
                              {column.format && typeof value === "number" ? column.format(value) : value}
                            </TableCell>
                          );
                        })}
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        }

      </DialogContent>
      <DialogActions>
        <Button onClick={onClosed} variant="contained" color="secondary" startIcon={<CloseIcon />}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionStatistics;
